import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Users, Plus, Pencil, Trash2, Search, GraduationCap, CheckCircle2, Clock, Globe, Building2, Infinity, X, ChevronLeft, ChevronRight, Eye, HardHat } from 'lucide-react';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== Utilidades para RUT Chileno ==============

// Limpia el RUT de cualquier formato (puntos, guiones, espacios)
const cleanRut = (rut) => {
  if (!rut) return '';
  return rut.replace(/[^0-9kK]/g, '').toUpperCase();
};

// Formatea el RUT con puntos y guión (12.345.678-9)
const formatRut = (rut) => {
  const cleaned = cleanRut(rut);
  if (cleaned.length < 2) return cleaned;
  
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  
  // Agregar puntos cada 3 dígitos de derecha a izquierda
  let formatted = '';
  for (let i = body.length - 1, count = 0; i >= 0; i--, count++) {
    if (count > 0 && count % 3 === 0) {
      formatted = '.' + formatted;
    }
    formatted = body[i] + formatted;
  }
  
  return `${formatted}-${dv}`;
};

// Valida el dígito verificador del RUT chileno
const validateRut = (rut) => {
  const cleaned = cleanRut(rut);
  if (cleaned.length < 2) return false;
  
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  
  // Calcular dígito verificador
  let sum = 0;
  let multiplier = 2;
  
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  const remainder = sum % 11;
  const calculatedDv = remainder === 0 ? '0' : remainder === 1 ? 'K' : String(11 - remainder);
  
  return dv === calculatedDv;
};

// Component for displaying person's training status in detail modal
function PersonaCapacitacionesDetalle({ personaId, isAdmin, onUpdate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCaps = useCallback(async function() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/personas/${personaId}/capacitaciones-requeridas`);
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [personaId]);

  useEffect(function() {
    if (personaId) fetchCaps();
  }, [personaId, fetchCaps]);

  const toggleCapacitacion = async function(capId, cumplida) {
    try {
      const action = cumplida ? 'desmarcar' : 'completar';
      await axios.post(`${API}/personas/${personaId}/capacitaciones/${capId}/${action}`);
      toast.success(cumplida ? 'Capacitación desmarcada' : 'Capacitación completada');
      fetchCaps();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error('Error al actualizar');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-b-2 border-teal-500 rounded-full"></div></div>;
  }

  if (!data || !data.capacitaciones || data.capacitaciones.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-8">No hay capacitaciones requeridas para esta persona</p>;
  }

  const caps = data.capacitaciones;
  const stats = data.resumen;

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Cumplimiento General</span>
          <span className="text-sm font-bold text-slate-900">{stats.cumplidas}/{stats.total} ({stats.porcentaje}%)</span>
        </div>
        <Progress value={stats.porcentaje} className="h-2" />
        <div className="flex gap-4 mt-3 text-xs">
          <span className="flex items-center gap-1 text-green-600">
            <CheckCircle2 className="w-3 h-3" />
            {stats.cumplidas} Cumplidas
          </span>
          <span className="flex items-center gap-1 text-red-600">
            <Clock className="w-3 h-3" />
            {stats.pendientes} Pendientes
          </span>
        </div>
      </div>

      {/* Training List */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
        {caps.map(function(cap) {
          const done = cap.cumplida;
          const bgClass = done ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
          const txtClass = done ? 'text-green-800' : 'text-red-800';
          
          return (
            <div key={cap.id} className={`flex gap-3 p-3 rounded-lg border ${bgClass}`} data-testid={`cap-item-${cap.id}`}>
              {isAdmin && (
                <button 
                  onClick={function() { toggleCapacitacion(cap.id, done); }} 
                  className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${done ? 'bg-green-500 text-white' : 'bg-white border-2 border-red-300'}`}
                  data-testid={`toggle-cap-${cap.id}`}
                >
                  {done && <CheckCircle2 className="w-3 h-3" />}
                </button>
              )}
              {!isAdmin && (
                <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${done ? 'bg-green-500 text-white' : 'bg-red-200'}`}>
                  {done && <CheckCircle2 className="w-3 h-3" />}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${txtClass}`}>{cap.nombre}</p>
                {cap.descripcion && (
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{cap.descripcion}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {cap.es_transversal ? (
                    <span className="text-[10px] flex items-center gap-0.5 text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                      <Globe className="w-2.5 h-2.5" />Transversal
                    </span>
                  ) : (
                    <span className="text-[10px] flex items-center gap-0.5 text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                      <Building2 className="w-2.5 h-2.5" />Área
                    </span>
                  )}
                  {cap.vigencia_meses ? (
                    <span className="text-[10px] flex items-center gap-0.5 text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                      <Clock className="w-2.5 h-2.5" />Vigencia: {cap.vigencia_meses} meses
                    </span>
                  ) : (
                    <span className="text-[10px] flex items-center gap-0.5 text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                      <Infinity className="w-2.5 h-2.5" />Indefinida
                    </span>
                  )}
                  {done && cap.fecha_realizacion && (
                    <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                      Completada: {new Date(cap.fecha_realizacion).toLocaleDateString('es-CL')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const Personas = () => {
  const { isAdmin, impersonate } = useAuth();
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [gerencias, setGerencias] = useState([]);
  const [superintendencias, setSuperintendencias] = useState([]);
  const [cumplimientos, setCumplimientos] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCapDialogOpen, setIsCapDialogOpen] = useState(false);
  const [selectedPersonaForCaps, setSelectedPersonaForCaps] = useState(null);
  const [editingPersona, setEditingPersona] = useState(null);
  const [rutError, setRutError] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    rut: '',
    sexo: '',
    fecha_nacimiento: '',
    gerencia_id: '',
    superintendencia_id: '',
    cargo_id: '',
    area_id: '',
    user_id: '',
    email: '',
    password: '',
    mostrar_en_organigrama: true
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, itemId: null, deps: [], loadingDeps: false });
  const [page, setPage] = useState(1);
  const [filterGerencia, setFilterGerencia] = useState('');
  const [filterSuperintendencia, setFilterSuperintendencia] = useState('');
  const [filterSups, setFilterSups] = useState([]);
  const [actividadesCatalog, setActividadesCatalog] = useState([]);
  const [personaActividades, setPersonaActividades] = useState({}); // {persona_id: [{id, nombre}]}
  const [actDialog, setActDialog] = useState({ open: false, persona: null, selected: [], searchTerm: '' });
  const PAGE_SIZE = 12;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/personas`),
        axios.get(`${API}/areas`),
        axios.get(`${API}/cargos`),
        axios.get(`${API}/gerencias`),
        axios.get(`${API}/actividades`),
        axios.get(`${API}/personas/actividades-bulk`)
      ]);
      const [personasRes, areasRes, cargosRes, gerenciasRes, actividadesRes, actBulkRes] = results;
      const endpoints = ['personas', 'areas', 'cargos', 'gerencias', 'actividades', 'actividades-bulk'];
      const failed = results
        .map((r, i) => r.status === 'rejected' ? { ep: endpoints[i], err: r.reason } : null)
        .filter(Boolean);
      if (personasRes.status === 'fulfilled') setPersonas(personasRes.value.data);
      if (areasRes.status === 'fulfilled') setAreas(areasRes.value.data);
      if (cargosRes.status === 'fulfilled') setCargos(cargosRes.value.data);
      if (gerenciasRes.status === 'fulfilled') setGerencias(gerenciasRes.value.data);
      if (actividadesRes.status === 'fulfilled') setActividadesCatalog(actividadesRes.value.data || []);
      if (actBulkRes.status === 'fulfilled') setPersonaActividades(actBulkRes.value.data || {});
      if (personasRes.status === 'rejected') {
        console.error('[Personas] Falló /personas:', personasRes.reason?.response?.status, personasRes.reason?.message);
        toast.error('No se pudieron cargar las personas. Intenta recargar.');
      } else if (failed.length > 0) {
        console.warn('[Personas] Endpoints con error (no críticos):', failed);
      }
    } catch (error) {
      console.error('[Personas] Error inesperado:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load cumplimientos only for visible page
  useEffect(() => {
    if (paginatedPersonas.length === 0) return;
    const idsToFetch = paginatedPersonas.filter(p => !cumplimientos[p.id]).map(p => p.id);
    if (idsToFetch.length === 0) return;
    
    let cancelled = false;
    Promise.all(
      idsToFetch.map(async (id) => {
        try {
          const res = await axios.get(`${API}/personas/${id}/capacitaciones-requeridas`);
          return { id, data: res.data.resumen };
        } catch {
          return { id, data: { total: 0, cumplidas: 0, porcentaje: 0 } };
        }
      })
    ).then(results => {
      if (cancelled) return;
      setCumplimientos(prev => {
        const updated = { ...prev };
        results.forEach(r => { updated[r.id] = r.data; });
        return updated;
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, personas.length]);

  const refreshCumplimiento = async (personaId) => {
    try {
      const res = await axios.get(`${API}/personas/${personaId}/capacitaciones-requeridas`);
      setCumplimientos(prev => ({
        ...prev,
        [personaId]: res.data.resumen
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const openCapacitacionesDialog = (persona) => {
    setSelectedPersonaForCaps(persona);
    setIsCapDialogOpen(true);
  };

  const openActividadesDialog = (persona) => {
    const current = personaActividades[persona.id] || [];
    setActDialog({
      open: true,
      persona,
      selected: current.map(a => a.id),
      searchTerm: '',
    });
  };

  const toggleActividad = (actividadId) => {
    setActDialog(prev => ({
      ...prev,
      selected: prev.selected.includes(actividadId)
        ? prev.selected.filter(x => x !== actividadId)
        : [...prev.selected, actividadId]
    }));
  };

  const saveActividades = async () => {
    if (!actDialog.persona) return;
    try {
      await axios.put(`${API}/personas/${actDialog.persona.id}/actividades`, {
        actividad_ids: actDialog.selected
      });
      const selected = actividadesCatalog
        .filter(a => actDialog.selected.includes(a.id))
        .map(a => ({ id: a.id, nombre: a.nombre }));
      setPersonaActividades(prev => ({ ...prev, [actDialog.persona.id]: selected }));
      toast.success('Actividades actualizadas');
      setActDialog({ open: false, persona: null, selected: [], searchTerm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar actividades');
    }
  };

  const removeActividadQuick = async (actividadId) => {
    setActDialog(prev => ({ ...prev, selected: prev.selected.filter(x => x !== actividadId) }));
  };

  const filteredPersonas = personas.filter(persona => {
    // Text search: match each word independently against nombre, apellido, rut
    if (search) {
      const searchLower = search.toLowerCase().trim();
      const searchable = `${persona.nombre} ${persona.apellido} ${persona.rut} ${formatRut(persona.rut)}`.toLowerCase();
      const words = searchLower.split(/\s+/);
      if (!words.every(word => searchable.includes(word))) return false;
    }
    // Gerencia filter
    if (filterGerencia && persona.gerencia_id !== filterGerencia) return false;
    // Superintendencia filter
    if (filterSuperintendencia && persona.superintendencia_id !== filterSuperintendencia) return false;
    return true;
  });

  const openCreateDialog = () => {
    setEditingPersona(null);
    setRutError('');
    setSuperintendencias([]);
    setFormData({ 
      nombre: '', apellido: '', rut: '', sexo: '', fecha_nacimiento: '',
      gerencia_id: '', superintendencia_id: '', cargo_id: '', area_id: '', 
      user_id: '', email: '', password: '', mostrar_en_organigrama: true 
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (persona) => {
    setEditingPersona(persona);
    setRutError('');
    const gId = persona.gerencia_id || '';
    setFormData({
      nombre: persona.nombre,
      apellido: persona.apellido,
      rut: formatRut(persona.rut),
      sexo: persona.sexo || '',
      fecha_nacimiento: persona.fecha_nacimiento || '',
      gerencia_id: gId,
      superintendencia_id: persona.superintendencia_id || '',
      cargo_id: persona.cargo_id || '',
      area_id: persona.area_id,
      user_id: persona.user_id || '',
      email: persona.email || '',
      password: '',
      mostrar_en_organigrama: persona.mostrar_en_organigrama !== false
    });
    // Load superintendencias for the selected gerencia
    if (gId) {
      axios.get(`${API}/superintendencias/gerencia/${gId}`).then(res => {
        setSuperintendencias(res.data);
      }).catch(() => setSuperintendencias([]));
    } else {
      setSuperintendencias([]);
    }
    setIsDialogOpen(true);
  };

  // Handler para cambio de RUT con formato automático
  const handleRutChange = (e) => {
    const value = e.target.value;
    const formatted = formatRut(value);
    setFormData({ ...formData, rut: formatted });
    
    // Validar el RUT si tiene suficientes caracteres
    if (cleanRut(value).length >= 2) {
      if (!validateRut(value)) {
        setRutError('RUT inválido');
      } else {
        setRutError('');
      }
    } else {
      setRutError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar RUT antes de enviar
    if (!validateRut(formData.rut)) {
      setRutError('El RUT ingresado no es válido');
      toast.error('El RUT ingresado no es válido');
      return;
    }
    
    // Proteger contra borrado accidental de cargo al editar
    if (editingPersona && editingPersona.cargo_id && !formData.cargo_id) {
      toast.error('Este trabajador tenía un cargo asignado. Debes seleccionar un cargo válido para la nueva área antes de guardar.');
      return;
    }
    
    try {
      const payload = {
        nombre: formData.nombre,
        apellido: formData.apellido,
        rut: formatRut(formData.rut),
        sexo: formData.sexo || null,
        fecha_nacimiento: formData.fecha_nacimiento || null,
        gerencia_id: formData.gerencia_id || null,
        superintendencia_id: formData.superintendencia_id || null,
        cargo_id: formData.cargo_id || null,
        area_id: formData.area_id,
        email: formData.email || null,
        password: formData.password || null,
        mostrar_en_organigrama: formData.mostrar_en_organigrama
      };
      if (editingPersona) {
        await axios.put(`${API}/personas/${editingPersona.id}`, payload);
        toast.success('Persona actualizada');
      } else {
        await axios.post(`${API}/personas`, payload);
        toast.success('Persona creada');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    setDeleteDialog(prev => ({ ...prev, open: true, itemId: id, deps: [], loadingDeps: true }));
    try {
      const res = await axios.get(`${API}/dependencias/personas/${id}`);
      setDeleteDialog(prev => ({ ...prev, deps: res.data.dependencias, loadingDeps: false }));
    } catch { setDeleteDialog(prev => ({ ...prev, loadingDeps: false })); }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/personas/${deleteDialog.itemId}`);
      toast.success('Persona eliminada');
      setDeleteDialog({ open: false, itemId: null, deps: [], loadingDeps: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
      setDeleteDialog({ open: false, itemId: null, deps: [], loadingDeps: false });
    }
  };

  const handleImpersonate = async (persona) => {
    try {
      await impersonate(persona.id);
      toast.success(`Impersonando a ${persona.nombre} ${persona.apellido}`);
      navigate('/portal/dashboard');
    } catch (error) {
      toast.error('Error al impersonar');
    }
  };

  const getAreaName = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    return area?.nombre || '-';
  };

  const getCargoName = (cargoId) => {
    const cargo = cargos.find(c => c.id === cargoId);
    return cargo?.nombre || '-';
  };

  const filteredCargos = formData.area_id 
    ? cargos.filter(c => !c.area_id || c.area_id === formData.area_id)
    : cargos;

  const totalPages = Math.ceil(filteredPersonas.length / PAGE_SIZE);
  const paginatedPersonas = filteredPersonas.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Personas</h1>
          <p className="text-slate-500">Gestión de trabajadores</p>
        </div>
        {isAdmin() && (
          <Button 
            onClick={openCreateDialog}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="create-persona-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Persona
          </Button>
        )}
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, apellido o RUT..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
                data-testid="search-personas"
              />
            </div>
            <Select
              value={filterGerencia || 'all'}
              onValueChange={(v) => {
                const val = v === 'all' ? '' : v;
                setFilterGerencia(val);
                setFilterSuperintendencia('');
                setPage(1);
                if (val) {
                  axios.get(`${API}/superintendencias/gerencia/${val}`).then(res => setFilterSups(res.data)).catch(() => setFilterSups([]));
                } else {
                  setFilterSups([]);
                }
              }}
            >
              <SelectTrigger className="w-[200px]" data-testid="filter-gerencia">
                <SelectValue placeholder="Todas las Gerencias" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las Gerencias</SelectItem>
                {gerencias.map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filterSuperintendencia || 'all'}
              onValueChange={(v) => { setFilterSuperintendencia(v === 'all' ? '' : v); setPage(1); }}
              disabled={!filterGerencia}
            >
              <SelectTrigger className="w-[220px]" data-testid="filter-superintendencia">
                <SelectValue placeholder={filterGerencia ? "Todas las Sup." : "Seleccione Gerencia"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las Superintendencias</SelectItem>
                {filterSups.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(filterGerencia || filterSuperintendencia) && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterGerencia(''); setFilterSuperintendencia(''); setFilterSups([]); setPage(1); }}>
                <X className="w-4 h-4 mr-1" /> Limpiar filtros
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-2">{filteredPersonas.length} persona(s) encontrada(s)</p>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">RUT</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead className="text-center w-[70px]">Nivel</TableHead>
                <TableHead className="text-center">Sexo</TableHead>
                <TableHead>Area</TableHead>
                <TableHead>Gerencia</TableHead>
                <TableHead className="text-center">Actividades</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedPersonas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-slate-500 py-8">
                    No hay personas registradas
                  </TableCell>
                </TableRow>
              ) : (
                paginatedPersonas.map((persona) => {
                  const cumplimiento = cumplimientos[persona.id] || { total: 0, cumplidas: 0, porcentaje: 0 };
                  const cumplimientoColor = cumplimiento.porcentaje >= 100 ? 'bg-green-100 text-green-700' 
                    : cumplimiento.porcentaje >= 50 ? 'bg-yellow-100 text-yellow-700' 
                    : 'bg-red-100 text-red-700';
                  
                  return (
                  <TableRow key={persona.id} data-testid={`persona-row-${persona.id}`}>
                    <TableCell className="font-mono text-sm w-[140px] whitespace-nowrap">{formatRut(persona.rut)}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-emerald-600">
                            {persona.nombre?.[0]}{persona.apellido?.[0]}
                          </span>
                        </div>
                        <span className="truncate">{persona.nombre} {persona.apellido}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{getCargoName(persona.cargo_id)}</TableCell>
                    <TableCell className="text-center">
                      {persona.cargo_nivel ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700">
                          {persona.cargo_nivel}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {persona.sexo ? (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${persona.sexo === 'M' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                          {persona.sexo}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm">{persona.area_nombre || '-'}</TableCell>
                    <TableCell className="text-sm">{persona.gerencia_nombre || '-'}</TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        const acts = personaActividades[persona.id] || [];
                        return (
                          <button
                            onClick={() => openActividadesDialog(persona)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-opacity hover:opacity-80 ${acts.length > 0 ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'}`}
                            data-testid={`view-acts-${persona.id}`}
                            title={acts.map(a => a.nombre).join(', ') || 'Sin actividades'}
                          >
                            <HardHat className="w-3 h-3" />
                            {acts.length}
                          </button>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isAdmin() && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleImpersonate(persona)}
                            data-testid={`impersonate-${persona.id}`}
                            title="Ver como trabajador"
                          >
                            <Eye className="w-4 h-4 text-teal-500" />
                          </Button>
                        )}
                        <Link to={`/reportes?persona=${persona.id}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`report-persona-${persona.id}`} title="Ver reportes">
                            <GraduationCap className="w-4 h-4 text-slate-500" />
                          </Button>
                        </Link>
                        {isAdmin() && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEditDialog(persona)}
                              data-testid={`edit-persona-${persona.id}`}
                              title="Editar"
                            >
                              <Pencil className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDelete(persona.id)}
                              data-testid={`delete-persona-${persona.id}`}
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <p className="text-sm text-slate-500">
                Mostrando {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filteredPersonas.length)} de {filteredPersonas.length}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let p;
                  if (totalPages <= 7) { p = i + 1; }
                  else if (page <= 4) { p = i + 1; }
                  else if (page >= totalPages - 3) { p = totalPages - 6 + i; }
                  else { p = page - 3 + i; }
                  return (
                    <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm"
                      className={p === page ? 'bg-teal-500 hover:bg-teal-600' : ''}
                      onClick={() => setPage(p)}
                    >{p}</Button>
                  );
                })}
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPersona ? 'Editar Persona' : 'Nueva Persona'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                  data-testid="persona-nombre-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="apellido">Apellido</Label>
                <Input
                  id="apellido"
                  value={formData.apellido}
                  onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                  required
                  data-testid="persona-apellido-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rut">RUT</Label>
              <Input
                id="rut"
                value={formData.rut}
                onChange={handleRutChange}
                placeholder="12.345.678-9"
                required
                data-testid="persona-rut-input"
                className={rutError ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {rutError && (
                <p className="text-xs text-red-500 mt-1">{rutError}</p>
              )}
              <p className="text-xs text-slate-500">Se formatea automáticamente con puntos y guión</p>
            </div>

            {/* Nuevos campos */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sexo">Sexo</Label>
                <Select
                  value={formData.sexo || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, sexo: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin especificar</SelectItem>
                    <SelectItem value="M">Masculino</SelectItem>
                    <SelectItem value="F">Femenino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="fecha_nacimiento">Fecha de Nacimiento</Label>
                <Input
                  id="fecha_nacimiento"
                  value={formData.fecha_nacimiento || ''}
                  onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                  placeholder="DD-MM-YYYY"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="gerencia_id">Gerencia</Label>
                <Select
                  value={formData.gerencia_id || 'none'}
                  onValueChange={(value) => {
                    const newGerenciaId = value === 'none' ? '' : value;
                    setFormData({ ...formData, gerencia_id: newGerenciaId, superintendencia_id: '' });
                    if (newGerenciaId) {
                      axios.get(`${API}/superintendencias/gerencia/${newGerenciaId}`).then(res => {
                        setSuperintendencias(res.data);
                      }).catch(() => setSuperintendencias([]));
                    } else {
                      setSuperintendencias([]);
                    }
                  }}
                >
                  <SelectTrigger data-testid="persona-gerencia-select">
                    <SelectValue placeholder="Seleccionar gerencia" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {gerencias.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="superintendencia_id">Superintendencia</Label>
                <Select
                  value={formData.superintendencia_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, superintendencia_id: value === 'none' ? '' : value })}
                  disabled={!formData.gerencia_id}
                >
                  <SelectTrigger data-testid="persona-superintendencia-select">
                    <SelectValue placeholder={formData.gerencia_id ? 'Seleccionar' : 'Seleccione gerencia primero'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {superintendencias.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="area">Área</Label>
              <Select
                value={formData.area_id}
                onValueChange={(value) => {
                  // Solo resetear cargo si el cargo actual NO pertenece a la nueva área
                  // Cargos sin area_id se consideran transversales (válidos para cualquier área)
                  const currentCargo = cargos.find(c => c.id === formData.cargo_id);
                  const cargoStillValid = currentCargo && (!currentCargo.area_id || currentCargo.area_id === value);
                  setFormData({
                    ...formData,
                    area_id: value,
                    cargo_id: cargoStillValid ? formData.cargo_id : '',
                  });
                  if (formData.cargo_id && !cargoStillValid) {
                    toast.warning('El cargo anterior no pertenece a la nueva área. Selecciona un nuevo cargo antes de guardar.');
                  }
                }}
              >
                <SelectTrigger data-testid="persona-area-select">
                  <SelectValue placeholder="Seleccionar área" />
                </SelectTrigger>
                <SelectContent>
                  {areas.map((area) => (
                    <SelectItem key={area.id} value={area.id}>
                      {area.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cargo">Cargo</Label>
              <Select
                value={formData.cargo_id || 'none'}
                onValueChange={(value) => setFormData({ ...formData, cargo_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger data-testid="persona-cargo-select">
                  <SelectValue placeholder="Seleccionar cargo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin cargo asignado</SelectItem>
                  {filteredCargos.map((cargo) => (
                    <SelectItem key={cargo.id} value={cargo.id}>
                      {cargo.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Acceso al Portal */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> Acceso al Portal del Trabajador
              </h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="email">Correo Electrónico</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="trabajador@empresa.com"
                    data-testid="persona-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">
                    {editingPersona ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña'}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password || ''}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingPersona ? '••••••••' : 'Contraseña inicial'}
                    data-testid="persona-password-input"
                  />
                </div>
              </div>
            </div>
            
            {/* Opciones de visualización */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="mostrar-organigrama" className="text-sm font-medium">Mostrar en Organigrama</Label>
                  <p className="text-xs text-slate-500">Si está desactivado, no aparecerá en la vista del organigrama</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="mostrar-organigrama"
                    checked={formData.mostrar_en_organigrama}
                    onChange={(e) => setFormData({ ...formData, mostrar_en_organigrama: e.target.checked })}
                    className="sr-only peer"
                    data-testid="mostrar-organigrama-toggle"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
                </label>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-500 hover:bg-teal-600" data-testid="save-persona-btn">
                {editingPersona ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Capacitaciones Detail Dialog */}
      <Dialog open={isCapDialogOpen} onOpenChange={setIsCapDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-teal-500" />
              Capacitaciones
            </DialogTitle>
            {selectedPersonaForCaps && (
              <p className="text-sm text-slate-500">
                {selectedPersonaForCaps.nombre} {selectedPersonaForCaps.apellido} - {getAreaName(selectedPersonaForCaps.area_id)}
              </p>
            )}
          </DialogHeader>
          {selectedPersonaForCaps && (
            <PersonaCapacitacionesDetalle 
              personaId={selectedPersonaForCaps.id} 
              isAdmin={isAdmin()}
              onUpdate={() => refreshCumplimiento(selectedPersonaForCaps.id)}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCapDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteDialog.open}
        onOpenChange={(v) => !v && setDeleteDialog({ open: false, itemId: null, deps: [], loadingDeps: false })}
        onConfirm={confirmDelete}
        title="Eliminar Persona"
        description="¿Esta seguro de eliminar esta persona? Esta accion no se puede deshacer."
        dependencies={deleteDialog.deps}
        loadingDeps={deleteDialog.loadingDeps}
      />

      {/* Actividades modal */}
      <Dialog open={actDialog.open} onOpenChange={(v) => !v && setActDialog({ open: false, persona: null, selected: [], searchTerm: '' })}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="actividades-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HardHat className="w-5 h-5 text-teal-600" />
              Actividades asignadas
            </DialogTitle>
            {actDialog.persona && (
              <p className="text-sm text-slate-500">{actDialog.persona.nombre} {actDialog.persona.apellido} &middot; {getCargoName(actDialog.persona.cargo_id)}</p>
            )}
          </DialogHeader>

          {/* Actividades seleccionadas (chips) */}
          <div className="px-1 pb-2">
            <p className="text-xs font-medium text-slate-500 mb-1.5 uppercase">Seleccionadas ({actDialog.selected.length})</p>
            {actDialog.selected.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Sin actividades asignadas aun.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {actividadesCatalog
                  .filter(a => actDialog.selected.includes(a.id))
                  .map(a => (
                    <span key={a.id} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-[11px] font-medium bg-teal-100 text-teal-800">
                      {a.nombre}
                      {isAdmin() && (
                        <button
                          onClick={() => removeActividadQuick(a.id)}
                          className="w-4 h-4 rounded-full hover:bg-teal-200 inline-flex items-center justify-center"
                          data-testid={`remove-actividad-${a.id}`}
                          title="Remover"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  ))}
              </div>
            )}
          </div>

          {/* Buscador */}
          {isAdmin() && (
            <div className="relative px-1 pt-2 border-t">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                placeholder="Buscar actividad..."
                value={actDialog.searchTerm}
                onChange={(e) => setActDialog(prev => ({ ...prev, searchTerm: e.target.value }))}
                className="pl-9 mt-2"
                data-testid="actividades-search"
              />
            </div>
          )}

          {/* Lista */}
          {isAdmin() && (
            <div className="flex-1 overflow-y-auto border rounded-lg mt-2">
              {actividadesCatalog
                .filter(a => !actDialog.searchTerm || a.nombre.toLowerCase().includes(actDialog.searchTerm.toLowerCase()))
                .map(act => {
                  const isSelected = actDialog.selected.includes(act.id);
                  return (
                    <button
                      key={act.id}
                      onClick={() => toggleActividad(act.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left border-b last:border-b-0 transition-colors ${isSelected ? 'bg-teal-50 hover:bg-teal-100' : 'hover:bg-slate-50'}`}
                      data-testid={`toggle-actividad-${act.id}`}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${isSelected ? 'bg-teal-500 border-teal-500' : 'border-slate-300'}`}>
                        {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm text-slate-800">{act.nombre}</span>
                    </button>
                  );
                })}
              {actividadesCatalog.filter(a => !actDialog.searchTerm || a.nombre.toLowerCase().includes(actDialog.searchTerm.toLowerCase())).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-6">Sin resultados</p>
              )}
            </div>
          )}

          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setActDialog({ open: false, persona: null, selected: [], searchTerm: '' })}>
              Cancelar
            </Button>
            {isAdmin() && (
              <Button onClick={saveActividades} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-actividades-btn">
                Guardar ({actDialog.selected.length})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};